/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import App from 'apmui/App';
import EventTags from 'apmui/EventTags';
import IPageModel from 'apmui/page';
import PageType from 'apmui/page/PageType';
import WebtopType from 'apmui/page/webtop/WebtopType';
import Object from 'apmui/util/Object';
import { IResourceModel, IResourceLaunchEventDetail } from 'apmui/resource';
import LaunchType from './LaunchType';
import ResourceType from './ResourceType';
import { LaunchStatus } from './WebtopResourceModel';
// As a side effect, this import registers NA dialog event handlers
import launchNAResource from './networkAccess/Launch';
import launchRDResource from './remoteDesktop/Launch';
import { isVdiResourceList } from './remoteDesktop/Model';
import launchFolder from './webtopFolder/Launch';

const app = App.get();

/* Resource types which require launch request serializing:
 * network access and app tunnel only due to possible client installation.
 */
const BLOCKING_RESOURCES = [ResourceType.APP_TUNNEL, ResourceType.NETWORK_ACCESS];

function launchResource({ resource, launchType }: IResourceLaunchEventDetail, page: IPageModel) {
    if (page.pageType !== PageType.WEBTOP) {
        return;
    }
    switch (resource.type) {
        case ResourceType.APP_TUNNEL:
        case ResourceType.NETWORK_ACCESS:
            launchNAResource(resource, page);
            break;
        case ResourceType.REMOTE_DESKTOP:
            launchRDResource(resource, page, launchType);
            break;
        case ResourceType.WEBTOP_FOLDER:
            launchFolder(resource, page);
            break;
        default:
            resource.launchResource();
    }
}

class LaunchQueue {
    static isBlocking(resource: IResourceModel): boolean {
        return BLOCKING_RESOURCES.indexOf(resource.type) >= 0;
    }

    private current?: IResourceLaunchEventDetail;
    private queue: IResourceLaunchEventDetail[] = [];

    launch(params: IResourceLaunchEventDetail, page: IPageModel) {
        if (LaunchQueue.isBlocking(params.resource)) {
            if ('status' in params.resource) {
                // check if the resource is already being launched
                if (params.resource.status === LaunchStatus.IN_PROGRESS) {
                    return;
                }
                params.resource.status = LaunchStatus.IN_PROGRESS;
            }
            if (this.current) {
                this.queue.push(params);
            } else {
                this.current = params;
                launchResource(this.current, page);
            }
        } else {
            launchResource(params, page);
        }
    }

    next(page: IPageModel) {
        this.current = this.queue.shift();
        if (this.current) {
            launchResource(this.current, page);
        }
    }
}

const launchQueue = new LaunchQueue();

/*
 * AutoLaunch resources when the loading process is complete
 */
app.subscribe(EventTags.WEBTOP_CONFIGURATION_COMPLETE, (_, page) => {
    if (page.pageType !== PageType.WEBTOP || !page.webtop.resources) {
        return;
    }
    const launchType = LaunchType.AUTOLAUNCH;

    const naResources = page.webtop.resources[ResourceType.NETWORK_ACCESS];
    if (naResources) {
        const naResourceList = Object.values(naResources);
        // override auto launch settings when NA-only webtop converted
        //to full webtop due to custom protocol launch usage
        if (page.webtop.config.type === WebtopType.NETWORK_ACCESS) {
            // auto launch only first item (but resources are requested
            // in reverse order), ignore autoLaunch on others
            app.launchResource(naResourceList[naResourceList.length - 1], { launchType });
        } else {
            naResourceList
                .filter((resource) => resource.autoLaunch)
                .map((resource) => app.launchResource(resource, { launchType }));
        }
    }
});

/*
 * AutoLaunch remote desktop resources when adding to store (could be after WEBTOP_CONFIGURATION_COMPLETE).
 */
app.subscribe(EventTags.RESOURCE_STORE_CHANGED, ({ detail }, page) => {
    if (page.pageType !== PageType.WEBTOP || !page.webtop.resources) {
        return;
    }
    const launchType = LaunchType.AUTOLAUNCH;
    const rdResources = detail[ResourceType.REMOTE_DESKTOP];
    if (rdResources) {
        Object.values(rdResources)
            .filter((resource) => (isVdiResourceList(resource) ? resource.autoLogon : resource.autoLaunch))
            .map((resource) => app.launchResource(resource, { launchType }));
    }
});

app.subscribeDefault(EventTags.LAUNCH_RESOURCE, ({ detail }, page) => {
    try {
        launchQueue.launch(detail, page);
    } catch (e) {
        throw new Error(`Failed to launch resource ${detail.resource.id} of type ${detail.resource.type}: ${e}.`);
    }
});

app.subscribeDefault(EventTags.RESOURCE_LAUNCH_COMPLETE, ({ detail: resource }, page) => {
    // Cleanup resource status indication
    if ('status' in resource && resource.status === LaunchStatus.IN_PROGRESS) {
        resource.status = LaunchStatus.INITIAL;
    }
    launchQueue.next(page);
});
